import React, { useState, useRef } from 'react';
import { COLORS } from '../App';
import { updateDeal } from '../api';
import TemplateSelector from './TemplateSelector';
import './DealForm.css';

const API_BASE = window.location.port === '3000'
  ? 'http://localhost:4100/api'
  : window.location.pathname.startsWith('/cmbs')
    ? '/cmbs/api'
    : '/api';

function apiFetch(path, opts = {}) {
  const token = localStorage.getItem('cmbs_token');
  const headers = { ...opts.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (opts.body && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    opts.body = JSON.stringify(opts.body);
  }
  return fetch(`${API_BASE}${path}`, { ...opts, headers }).then(r => r.json());
}

const PROPERTY_TYPES = ['Office', 'Multifamily', 'Retail', 'Industrial', 'Hospitality', 'Mixed-Use', 'Self-Storage', 'Mobile Home Park', 'Student Housing', 'Senior Living', 'Healthcare'];
const BROKERAGES = ['JLL', 'CBRE', 'Marcus & Millichap', 'Cushman & Wakefield', 'Newmark', 'Eastdil Secured', 'HFF', 'Berkadia', 'Walker & Dunlop', 'Other'];

export default function DealForm({ deal, onCreated, onCancel }) {
  const [form, setForm] = useState({
    property_name:   deal?.property_name   || '',
    address:         deal?.address         || '',
    city:            deal?.city            || '',
    state:           deal?.state           || '',
    property_type:   deal?.property_type   || '',
    brokerage:       deal?.brokerage       || '',
    broker_contact:  deal?.broker_contact  || '',
    originator_name: deal?.originator_name || '',
    loan_amount:     deal?.loan_amount     || '',
    email_body:      deal?.email_body      || '',
    notes:           deal?.notes           || '',
  });

  const [parsing,              setParsing]              = useState(false);
  const [saving,               setSaving]               = useState(false);
  const [parseResult,          setParseResult]          = useState(null);
  const [createdDeal,          setCreatedDeal]          = useState(null);
  const [templateMatch,        setTemplateMatch]        = useState(null);
  const [emailMode,            setEmailMode]            = useState('paste');
  const [emailText,            setEmailText]            = useState('');
  const [emailFile,            setEmailFile]            = useState(null);
  const [omFile,               setOmFile]               = useState(null);
  const [emailTempPath,        setEmailTempPath]        = useState(null);
  const [emailOriginalFilename,setEmailOriginalFilename]= useState(null);
  const [omTempPath,           setOmTempPath]           = useState(null);
  const [omOriginalFilename,   setOmOriginalFilename]   = useState(null);

  const fileInputRef = useRef(null);
  const omInputRef   = useRef(null);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const canParse =
    (emailMode === 'paste'  && emailText.trim().length > 0) ||
    (emailMode === 'upload' && !!emailFile) ||
    !!omFile;

  async function handleParseDocuments() {
    setParsing(true);
    setParseResult(null);
    try {
      const token = localStorage.getItem('cmbs_token');
      const hasFile = (emailMode === 'upload' && emailFile) || omFile;
      let res;

      if (hasFile) {
        const formData = new FormData();
        if (emailMode === 'upload' && emailFile) {
          formData.append('email_file', emailFile);
        } else if (emailMode === 'paste' && emailText.trim()) {
          formData.append('email_text', emailText);
        }
        if (omFile) formData.append('om_file', omFile);
        res = await fetch(`${API_BASE}/intake/parse-documents`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
      } else {
        res = await fetch(`${API_BASE}/intake/parse-documents`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ email_text: emailText })
        });
      }

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText);
      }

      const result = await res.json();
      setParseResult(result);

      if (result.email?.temp_file_path) {
        setEmailTempPath(result.email.temp_file_path);
        setEmailOriginalFilename(result.email.original_filename);
      }
      if (result.om?.temp_file_path) {
        setOmTempPath(result.om.temp_file_path);
        setOmOriginalFilename(result.om.original_filename);
      }

      // Single setForm call to avoid stale-closure overwrites
      const merged = result.merged || {};
      setForm(prev => ({
        ...prev,
        property_name:   merged.property_name   || prev.property_name,
        address:         merged.address          || prev.address,
        city:            merged.city             || prev.city,
        state:           merged.state            || prev.state,
        property_type:   merged.property_type    || prev.property_type,
        loan_amount:     merged.loan_amount      || prev.loan_amount,
        brokerage:       merged.brokerage        || prev.brokerage,
        broker_contact:  merged.broker_contact   || prev.broker_contact,
        originator_name: merged.originator_name  || prev.originator_name,
        notes:           merged.notes            || prev.notes,
        email_body:      result.email?.body      || prev.email_body,
      }));
    } catch (err) {
      console.error('Parse error:', err);
      alert(`Parse failed: ${err.message}`);
    } finally {
      setParsing(false);
    }
  }

  async function handleSubmit() {
    // Edit mode
    if (deal) {
      setSaving(true);
      try {
        await updateDeal(deal.id, form);
        onCreated();
      } catch (err) {
        alert('Failed to update deal: ' + err.message);
      }
      setSaving(false);
      return;
    }

    // New deal validation
    const hasDocument = !!(
      emailTempPath ||
      (emailMode === 'paste' && emailText.trim()) ||
      omTempPath
    );
    if (!hasDocument) {
      alert('Please upload a broker email or an Offering Memorandum, then click "Parse and Auto-Fill" before creating the deal.');
      return;
    }
    if (!form.property_name?.trim()) {
      alert('Property name is required. Either parse the documents to auto-fill it, or type it manually.');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('cmbs_token');
      const body = {
        ...form,
        email_body:            form.email_body || (emailMode === 'paste' ? emailText : null),
        email_parsed_data:     parseResult?.merged || null,
        om_temp_file_path:     omTempPath     || null,
        om_original_filename:  omOriginalFilename || null,
        email_temp_file_path:  emailMode === 'upload' ? emailTempPath : null,
        email_original_filename: emailMode === 'upload' ? emailOriginalFilename : null,
      };

      const res = await fetch(`${API_BASE}/intake/pipe-to-dmp`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error(await res.text());
      const result = await res.json();
      const dealRow = result.data;

      if (result.templateMatch && result.templateMatch.status !== 'auto_assigned') {
        setCreatedDeal(dealRow);
        setTemplateMatch(result.templateMatch);
      } else {
        onCreated(dealRow);
      }
    } catch (err) {
      alert(`Could not create deal: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  const card        = { background: COLORS.white, borderRadius: 8, border: `1px solid ${COLORS.border}`, padding: 24, marginBottom: 20 };
  const inputStyle  = { width: '100%', padding: '8px 12px', border: `1px solid ${COLORS.border}`, borderRadius: 4, fontSize: 14 };
  const labelStyle  = { display: 'block', marginBottom: 4, fontSize: 12, color: COLORS.textLight, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' };
  const fieldStyle  = { marginBottom: 16 };

  // Template selection screen (after deal creation when no auto-match)
  if (createdDeal && templateMatch) {
    const isMultiple = templateMatch.status === 'multiple_available';
    const templates  = isMultiple ? templateMatch.templates : (templateMatch.allTemplates || []);

    const handleAssign = async (templateId) => {
      const res = await apiFetch(`/deals/${createdDeal.id}/template`, { method: 'PUT', body: { template_id: templateId } });
      if (res.success) { onCreated({ ...createdDeal, template_id: templateId }); }
      else alert(res.error || 'Failed to assign template');
    };

    return (
      <div>
        <div style={{ background: '#E8F5E9', borderLeft: '4px solid #4CAF50', borderRadius: 8, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#2E7D32' }}>Deal Created: {createdDeal.property_name}</div>
          <div style={{ fontSize: 13, color: '#4CAF50', marginTop: 4 }}>DMP ID: {createdDeal.dmp_deal_id || 'Assigned'}</div>
        </div>
        <div style={{
          background: isMultiple ? '#FFF8E1' : '#FFEBEE',
          borderLeft: `4px solid ${isMultiple ? '#FFC107' : '#EF5350'}`,
          borderRadius: 8, padding: '16px 20px', marginBottom: 20,
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: isMultiple ? '#F57F17' : '#C62828', marginBottom: 8 }}>
            {isMultiple ? 'Select UW Template' : `No UW Template for "${form.property_type}"`}
          </div>
          <div style={{ fontSize: 13, color: COLORS.textLight, marginBottom: 16 }}>
            {isMultiple
              ? `Multiple templates available for ${form.property_type}. Select one to continue.`
              : 'No active template matches this property type. Select from available templates or upload a new one in Admin > UW Templates.'}
          </div>
          <TemplateSelector
            templates={templates}
            selectedId={null}
            onSelect={handleAssign}
            propertyType={form.property_type}
            onSkip={() => onCreated(createdDeal)}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <h3 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy }}>{deal ? 'Edit Deal' : 'New Deal Intake'}</h3>
        <button onClick={onCancel} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${COLORS.border}`, borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
          Back to Pipeline
        </button>
      </div>

      {/* ── Document Inputs (new deal only) ── */}
      {!deal && (
        <div className="document-inputs-section">
          <h3>Document Inputs</h3>
          <p className="section-subtitle">
            Upload at least one of the following. TrancheDesk will parse them and auto-fill the deal details below.
          </p>

          <div className="document-inputs-grid">
            {/* Broker Email card */}
            <div className="doc-input-card">
              <div className="doc-input-card-header">
                <strong>Broker Email</strong>
                <span className="doc-input-card-tag">Optional if OM provided</span>
              </div>
              <div className="email-mode-tabs">
                {[['paste', 'Paste Text'], ['upload', 'Upload File (.eml / .pdf)'], ['skip', 'Skip']].map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    className={`tab${emailMode === mode ? ' active' : ''}`}
                    onClick={() => setEmailMode(mode)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {emailMode === 'paste' && (
                <textarea
                  className="email-paste-textarea"
                  value={emailText}
                  onChange={e => setEmailText(e.target.value)}
                  placeholder="Paste the broker's email text here..."
                  rows={8}
                />
              )}

              {emailMode === 'upload' && (
                <div
                  className="file-drop-area"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  style={{ cursor: 'pointer' }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".eml,.pdf"
                    style={{ display: 'none' }}
                    onChange={e => setEmailFile(e.target.files?.[0] || null)}
                  />
                  {emailFile ? (
                    <div className="file-selected">
                      {emailFile.name} ({Math.round(emailFile.size / 1024)} KB)
                    </div>
                  ) : (
                    <div style={{ fontWeight: 600, color: COLORS.navy, marginBottom: 4 }}>
                      Drop .eml or .pdf here, or click to browse
                    </div>
                  )}
                  <small>Accepted: .eml or .pdf</small>
                </div>
              )}

              {emailMode === 'skip' && (
                <div className="skip-notice">
                  You can add the broker email later from the Deal Notes page.
                </div>
              )}
            </div>

            {/* Offering Memorandum card */}
            <div className="doc-input-card">
              <div className="doc-input-card-header">
                <strong>Offering Memorandum</strong>
                <span className="doc-input-card-tag">Optional if email provided</span>
              </div>
              <div
                className="file-drop-area"
                onClick={() => omInputRef.current && omInputRef.current.click()}
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="file"
                  ref={omInputRef}
                  accept=".pdf"
                  style={{ display: 'none' }}
                  onChange={e => setOmFile(e.target.files?.[0] || null)}
                />
                {omFile ? (
                  <div className="file-selected">
                    {omFile.name} ({Math.round(omFile.size / 1024)} KB)
                  </div>
                ) : (
                  <div style={{ fontWeight: 600, color: COLORS.navy, marginBottom: 4 }}>
                    Drop PDF here, or click to browse
                  </div>
                )}
                <small>
                  Property header info extracted at intake. Full OM analysis runs later in Deal Notes.
                </small>
              </div>
              {omFile && (
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: '#059669', fontWeight: 500 }}>OM selected</span>
                  <button
                    type="button"
                    onClick={() => { setOmFile(null); setOmTempPath(null); setOmOriginalFilename(null); }}
                    style={{ fontSize: 11, color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Parse button */}
          <div className="parse-button-row">
            <button
              type="button"
              className="btn-parse"
              onClick={handleParseDocuments}
              disabled={parsing || !canParse}
            >
              {parsing ? 'Parsing...' : 'Parse and Auto-Fill from Documents'}
            </button>
          </div>

          {/* Extracted highlights */}
          {parseResult && (
            <div className="extracted-highlights">
              <h4>Extracted Highlights</h4>
              <div className="extracted-grid">
                {parseResult.merged?.property_name && (
                  <div><strong>Property:</strong> {parseResult.merged.property_name}</div>
                )}
                {parseResult.merged?.address && (
                  <div>
                    <strong>Address:</strong> {parseResult.merged.address}
                    {parseResult.merged.city ? `, ${parseResult.merged.city}` : ''}
                    {parseResult.merged.state ? `, ${parseResult.merged.state}` : ''}
                  </div>
                )}
                {parseResult.merged?.property_type && (
                  <div><strong>Type:</strong> {parseResult.merged.property_type}</div>
                )}
                {parseResult.merged?.loan_amount && (
                  <div><strong>Loan Request:</strong> ${Number(parseResult.merged.loan_amount).toLocaleString()}</div>
                )}
                {parseResult.merged?.sponsor_name && (
                  <div><strong>Sponsor:</strong> {parseResult.merged.sponsor_name}</div>
                )}
                {parseResult.merged?.brokerage && (
                  <div><strong>Brokerage:</strong> {parseResult.merged.brokerage}</div>
                )}
                {parseResult.om?.status === 'not_supported' && (
                  <div style={{ gridColumn: '1 / -1', color: '#92400e', fontSize: 12 }}>
                    OM note: {parseResult.om.reason}
                  </div>
                )}
              </div>
              <small className="extracted-note">
                All extracted fields have been pre-filled in Deal Details below. Review and edit as needed.
              </small>
            </div>
          )}
        </div>
      )}

      {/* ── Deal Details ── */}
      <div style={card}>
        <h4 style={{ fontFamily: 'Georgia, serif', color: COLORS.navy, marginBottom: 16 }}>Deal Details</h4>
        {parseResult && (
          <p style={{ fontSize: 12, color: '#047857', marginBottom: 16, fontStyle: 'italic' }}>
            Auto-filled from documents. Edit any field as needed.
          </p>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div style={fieldStyle}>
            <label style={labelStyle}>Property Name</label>
            <input
              value={form.property_name}
              onChange={e => set('property_name', e.target.value)}
              style={inputStyle}
              placeholder="Auto-filled from documents, or type here"
            />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Loan Amount</label>
            <input
              type="number"
              value={form.loan_amount}
              onChange={e => set('loan_amount', e.target.value)}
              style={inputStyle}
              placeholder="0.00"
            />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Address</label>
            <input value={form.address} onChange={e => set('address', e.target.value)} style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>City</label>
              <input value={form.city} onChange={e => set('city', e.target.value)} style={inputStyle} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>State</label>
              <input value={form.state} onChange={e => set('state', e.target.value)} style={inputStyle} maxLength={2} placeholder="NY" />
            </div>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Property Type</label>
            <select value={form.property_type} onChange={e => set('property_type', e.target.value)} style={inputStyle}>
              <option value="">Select...</option>
              {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Brokerage</label>
            <select value={form.brokerage} onChange={e => set('brokerage', e.target.value)} style={inputStyle}>
              <option value="">Select...</option>
              {BROKERAGES.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Broker Contact</label>
            <input value={form.broker_contact} onChange={e => set('broker_contact', e.target.value)} style={inputStyle} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Originator</label>
            <input value={form.originator_name} onChange={e => set('originator_name', e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>Notes / Deal Highlights</label>
          <textarea
            value={form.notes}
            onChange={e => set('notes', e.target.value)}
            rows={form.notes ? 8 : 3}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: "'Consolas', monospace", fontSize: 12, lineHeight: 1.5 }}
            placeholder="Deal highlights will populate here after parsing. You can also type notes manually."
          />
        </div>
      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={handleSubmit}
          disabled={saving || parsing}
          style={{ padding: '12px 24px', background: COLORS.gold, color: COLORS.navy, border: 'none', borderRadius: 4, fontWeight: 600, cursor: 'pointer', fontSize: 14, opacity: (saving || parsing) ? 0.5 : 1 }}
        >
          {saving ? 'Saving...' : deal ? 'Update Deal' : 'Create Deal'}
        </button>
      </div>
    </div>
  );
}
